import {
  createNestablePublicClientApplication,
  InteractionRequiredAuthError,
  type IPublicClientApplication,
  type SilentRequest,
} from '@azure/msal-browser';
import { API_SCOPE, AUTHORITY, CLIENT_ID } from '../config';

/**
 * Nested App Authentication (NAA) for Outlook. Outlook already has the user's Entra
 * session; we ask it to broker a token for our API rather than prompting for a password.
 *
 * Falls back to a popup only when silent acquisition genuinely needs interaction - first
 * consent, for example. A redirect would leave the task pane and never come back cleanly.
 */

let pcaPromise: Promise<IPublicClientApplication> | null = null;

function getPca(): Promise<IPublicClientApplication> {
  pcaPromise ??= createNestablePublicClientApplication({
    auth: {
      clientId: CLIENT_ID,
      authority: AUTHORITY,
      // NAA brokers through Outlook; these redirects are still required by MSAL's config
      // validation and match the SPA URIs registered on the app.
      redirectUri: window.location.origin + '/taskpane.html',
    },
    cache: {
      cacheLocation: 'sessionStorage',
    },
  });
  return pcaPromise;
}

const tokenRequest: SilentRequest = {
  scopes: [API_SCOPE],
};

/**
 * Returns a Bearer token for the Function App. Prefer silent; popup only when MSAL says
 * interaction is required.
 */
export async function acquireApiToken(): Promise<string> {
  const pca = await getPca();

  try {
    const result = await pca.acquireTokenSilent(tokenRequest);
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      const result = await pca.acquireTokenPopup(tokenRequest);
      return result.accessToken;
    }
    throw error;
  }
}
